import React, { useState, useEffect, useMemo, useCallback } from "react";
import * as dateFns from "date-fns";
import { useTranslation } from "react-i18next";

import Modal from "../../common/Modal";
import Button from "../../common/FormFields/Button";
import Search from "../../common/FormFields/Search";
import CustomSelect from "../../common/FormFields/Select";
import {
  prepareTimeslotForUpdating,
  validateExceptionHoursOnDates,
} from "./helpers";
import {
  getStartOfWorkDay,
  getTimeIntervals,
  timestampToIso,
  timestampToWorkingTime,
  workingTimeToTimestamp,
  getAppointmentTypeDisplayName,
} from "../../../services/helpers";
import { ALL_BLOOD_TYPES } from "./constants";
import { get, preCheckBooking } from '../../../services/fetch';

const PREFIX = 'configuration.settings.bookAppointmentModal';

const BookAppointment = (props) => {
  const {
    selectedTimeslot,
    bloodBankId,
    bloodBankName,
    bookAppointmentModalIsOpen,
    toggleBookAppointmentModal,
    userByPnr,
    getUserByPnr,
    bookAppointment,
    cancelAppointmentBooking,
    appointmentTypes,
    startOverwriteTimeslot,
    finishOverwriteTimeslot,
    currentAppointment,
    isDayPage,
    updateDonor,
    isSlotClickFromCell,
    selectedTime,
    selectedResourceId,
    currentDate,
    setTimeslotRequest,
    setIsDirectlyBooking,
    calendar,
  } = props;
  const { t } = useTranslation();
  const [userPnr, setUserPnr] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [currentAppointments, setCurrentAppointments] = useState([]);
  const [chosenAppointment, setChosenAppointment] = useState(null);
  const [isDonorStatusPending, setIsDonorStatusPending] = useState(false);
  const [isConsultationAppointment, setIsConsultationAppointment] = useState(false);
  const [availableTimeSlots] = useState(getTimeIntervals());
  const [timeFrom, setTimeFrom] = useState(availableTimeSlots[0]);
  const [timeTo, setTimeTo] = useState(availableTimeSlots[0]);
  const [selectedAppointmentType, setSelectedAppointmentType] = useState(null);
  const [overlapModalOpen, setOverlapModalOpen] = useState(false);
  const [eligibleAppointmentTypes, setEligibleAppointmentTypes] = useState([]);
  const [bookingEligibilityMissing, setBookingEligibilityMissing] = useState(false);
  const [eligibilityCheckLoading, setEligibilityCheckLoading] = useState(false);

  const [inlineWarnings, setInlineWarnings] = useState([]);
  const [inlineOverrideAllowed, setInlineOverrideAllowed] = useState(true);
  const [warningsLoading, setWarningsLoading] = useState(false);

  const isNewRegistrationOrPendingRegistration =
    selectedAppointmentType?.donorStatus === "new" ||
    selectedAppointmentType?.donorStatus === "pending";

  const resourceTimeslots = useMemo(() => {
    if (!calendar) return [];
    const resourceId = selectedTimeslot?.resourceId || selectedResourceId;
    if (!resourceId) return [];
    const resource = calendar.find((res) => res.resourceId === resourceId);
    return resource?.timeslots || [];
  }, [calendar, selectedResourceId, selectedTimeslot]);

  const buildViolations = useCallback((failedChecks = [], details = {}) => {
    const violations = [];

    failedChecks.forEach((check) => {
      const detail = details?.[check] || {};
      const itemOverridable = detail.overrideAllowed === true;

      switch (check) {
        case 'earliestDate': {
          const dateStr = detail.value || '';
          violations.push({
            key: check,
            message: t(`${PREFIX}.forceBookCheckEarliestDate`, { date: dateStr }),
            overridable: itemOverridable,
          });
          break;
        }
        case 'donorQuarantine': {
          const raw = detail.value;
          const dateStr = raw
            ? new Date(raw).toLocaleDateString('no-NO', { year: 'numeric', month: '2-digit', day: '2-digit' })
            : '';
          violations.push({
            key: check,
            message: t(`${PREFIX}.forceBookCheckQuarantine`, { date: dateStr }),
            overridable: itemOverridable,
          });
          break;
        }
        case 'aboRh': {
          const mismatch = detail.mismatchType;
          let msgKey;
          if (mismatch === 'both') {
            msgKey = `${PREFIX}.forceBookCheckAboRhdMismatch`;
          } else if (mismatch === 'rhd') {
            msgKey = `${PREFIX}.forceBookCheckRhdMismatch`;
          } else {
            msgKey = `${PREFIX}.forceBookCheckAboMismatch`;
          }
          violations.push({ key: check, message: t(msgKey), overridable: itemOverridable });
          break;
        }
        case 'isEligible': {
          const reason = detail.reason;
          const msgKey = reason === 'missingEligibilityEntry'
            ? `${PREFIX}.forceBookCheckMissingEligibility`
            : `${PREFIX}.forceBookCheckNotEligible`;
          violations.push({ key: check, message: t(msgKey), overridable: itemOverridable });
          break;
        }
        case 'appointmentTypeMismatch': {
          violations.push({
            key: check,
            message: t(`${PREFIX}.forceBookCheckAppointmentTypeMismatch`, {
              originalType: detail.originalType || '',
              selectedType: detail.selectedType || '',
            }),
            overridable: true,
          });
          break;
        }
        case 'timeslotRange': {
          violations.push({
            key: check,
            message: t(`${PREFIX}.forceBookCheckTimeslotRange`),
            overridable: false,
          });
          break;
        }
        case 'existingAppointment': {
          const raw = detail.value;
          const dateStr = raw
            ? new Date(raw).toLocaleDateString('no-NO', { year: 'numeric', month: '2-digit', day: '2-digit' })
            : '';
          const timeStr = raw
            ? new Date(raw).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' })
            : '';
          violations.push({
            key: check,
            message: t(`${PREFIX}.forceBookCheckExistingAppointment`, { date: dateStr, time: timeStr }),
            overridable: true,
          });
          break;
        }
        default:
          violations.push({
            key: check,
            message: t(`${PREFIX}.forceBookCheckUnknown`, { check }),
            overridable: itemOverridable,
          });
      }
    });

    return violations;
  }, [t]);

  useEffect(() => {
    if (!currentUser || !selectedAppointmentType) {
      setInlineWarnings([]);
      setInlineOverrideAllowed(true);
      return;
    }
    if (!isSlotClickFromCell && !chosenAppointment) {
      setInlineWarnings([]);
      setInlineOverrideAllowed(true);
      return;
    }

    let cancelled = false;
    const runPreCheck = async () => {
      setWarningsLoading(true);
      let failedChecks = [];
      let details = {};
      let overrideAllowed = true;

      const requestBody = getTimeslotRequestBody();
      const appointmentValue = isSlotClickFromCell ? requestBody?.startDate : chosenAppointment?.value;
      const bookingStart = Number(appointmentValue);

      try {
        await preCheckBooking({
          donorUid: currentUser.id,
          appointmentTypeId: selectedAppointmentType.appointmentTypeId,
          appointmentStartDate: bookingStart,
          donorType: requestBody?.donorType,
        });
      } catch (preCheckErr) {
        if (preCheckErr?.body?.error === 'failed_checks') {
          failedChecks = preCheckErr.body.failedChecks || [];
          details = preCheckErr.body.details || {};
          overrideAllowed = preCheckErr.body.overrideAllowed !== false;
        }
      }

      const timeSlotData = selectedTimeslot;
      const needsCustomTimeslot =
        !isSlotClickFromCell &&
        (selectedAppointmentType.appointmentTypeId !== timeSlotData?.appointmentType?.appointmentTypeId ||
          selectedAppointmentType.duration !== timeSlotData?.appointmentType?.duration);

      if (needsCustomTimeslot) {
        failedChecks = ['appointmentTypeMismatch', ...failedChecks];
        details = {
          ...details,
          appointmentTypeMismatch: {
            ok: false,
            originalType: timeSlotData?.appointmentType?.name || '',
            selectedType: selectedAppointmentType?.name || '',
            overrideAllowed: true,
          },
        };
      }

      const hasExistingAppointment = currentUser?.nextAppointment != null;
      if (hasExistingAppointment) {
        failedChecks = [...failedChecks, 'existingAppointment'];
        details = {
          ...details,
          existingAppointment: {
            ok: false,
            value: currentUser.nextAppointment.startDate,
            overrideAllowed: true,
          },
        };
      }

      if (cancelled) return;

      const violations = buildViolations(failedChecks, details);
      setInlineWarnings(violations);
      setInlineOverrideAllowed(overrideAllowed);
      setWarningsLoading(false);
    };

    runPreCheck();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, selectedAppointmentType, chosenAppointment, isSlotClickFromCell]);

  const getOverlapInfo = ({
    bookingStart,
    bookingEnd,
    currentTimeslotId,
    includeSelf,
  }) => {
    const overlaps = [];
    resourceTimeslots.forEach((slot) => {
      (slot.appointments || []).forEach((app) => {
        const isSameAppointment =
          app.startDate === bookingStart && slot.timeslotId === currentTimeslotId;
        if (!includeSelf && isSameAppointment) return;
        const appStart = app.startDate;
        const appEnd = app.endDate;
        const isOverlap = appStart < bookingEnd && appEnd > bookingStart;
        if (isOverlap) {
          overlaps.push({
            timeslot: slot,
            appointment: app,
            status: app.status,
            hasReservation: !!app.reservation,
          });
        }
      });
    });
    const isReserved = (item) =>
      item.hasReservation || (item.status && item.status !== "pending");

    const reservedOverlaps = overlaps.filter(isReserved);
    const availableOverlaps = overlaps.filter((item) => !isReserved(item));

    return { reservedOverlaps, availableOverlaps };
  };

  const addExceptionToTimeslot = async (slot, overlapAppointments) => {
    const base = {...prepareTimeslotForUpdating(slot), rule: slot.initialSettings};
    const existingExceptions = base.exceptionHoursOnDates || [];
    const updatedExceptions = [...existingExceptions];

    overlapAppointments.forEach(({ appointment }) => {
      const exception = {
        from: timestampToIso(appointment.startDate),
        to: timestampToIso(appointment.endDate),
        date: timestampToIso(getStartOfWorkDay(appointment.startDate)),
      };
      updatedExceptions.push(exception);
    });

    const validatedExceptions = validateExceptionHoursOnDates(updatedExceptions);
    const payload = {
      ...base,
      exceptionHoursOnDates: validatedExceptions,
    };

    await setTimeslotRequest(payload, slot.appointmentType?.duration);
  };

  const applyOverlapExceptions = async (availableOverlaps) => {
    if (!availableOverlaps || availableOverlaps.length === 0) return;
    const grouped = availableOverlaps.reduce((acc, item) => {
      const key = item.timeslot.timeslotId;
      acc[key] = acc[key] ? [...acc[key], item] : [item];
      return acc;
    }, {});

    for (const key of Object.keys(grouped)) {
      await addExceptionToTimeslot(grouped[key][0].timeslot, grouped[key]);
    }
  };

  const handleOverlaps = async ({
    bookingStart,
    bookingEnd,
    currentTimeslotId,
    applyAvailableExceptions,
    deferExceptions = false,
  }) => {
    if(!isSlotClickFromCell && isConsultationAppointment) {
      return { blocked: false, hadAvailableOverlaps: false, deferredOverlaps: [] };
    }

    const { reservedOverlaps, availableOverlaps } = getOverlapInfo({
      bookingStart,
      bookingEnd,
      currentTimeslotId,
      includeSelf: !!applyAvailableExceptions,
    });

    if (reservedOverlaps.length > 0) {
      setOverlapModalOpen(true);
      return { blocked: true, hadAvailableOverlaps: false, deferredOverlaps: [] };
    }

    const exceptableOverlaps = applyAvailableExceptions
      ? availableOverlaps
      : availableOverlaps.filter((item) => {
          const isSelfAppointment =
            item.timeslot.timeslotId === currentTimeslotId &&
            item.appointment.startDate === bookingStart;
          return !isSelfAppointment;
        });

    if (exceptableOverlaps.length > 0 && !deferExceptions) {
      await applyOverlapExceptions(exceptableOverlaps);
    }

    return {
      blocked: false,
      hadAvailableOverlaps: availableOverlaps.length > 0,
      deferredOverlaps: deferExceptions ? exceptableOverlaps : [],
    };
  };

  useEffect(() => {
    if(selectedResourceId && selectedAppointmentType) {
      const newTimeFrom = availableTimeSlots.find(item => item.value === selectedTime) || availableTimeSlots[0];
      setTimeFrom(newTimeFrom);
      const newTimeToInMilliseconds = workingTimeToTimestamp(newTimeFrom, currentDate) + selectedAppointmentType.duration;
      setTimeTo(timestampToWorkingTime(newTimeToInMilliseconds));
    }
  }, [availableTimeSlots, currentDate, selectedAppointmentType, selectedResourceId, selectedTime])

  useEffect(() => {
    if (userPnr) {
      getUserByPnr(userPnr);
    }
  }, [getUserByPnr, userPnr]);

  useEffect(() => {
    if (!userByPnr) {
      setCurrentUser(null);
      return;
    }
    const donorBelongsToCurrentBank = 
      userByPnr.donationSiteIds && 
      userByPnr.donationSiteIds.includes(bloodBankId);

    if (donorBelongsToCurrentBank) {
      setCurrentUser(userByPnr);
    } else {
      setCurrentUser(null);
    }
  }, [userByPnr, bloodBankId]);

  useEffect(() => {
    if (currentUser && !isSlotClickFromCell) {
      setIsDonorStatusPending(currentUser?.status === "pending");
    }

    if(currentUser && isSlotClickFromCell) {
      setIsConsultationAppointment(currentUser?.status === "pending");
      setIsDonorStatusPending(currentUser?.status === "pending");
    }
  }, [currentDate, currentUser, isConsultationAppointment, isSlotClickFromCell, selectedTimeslot, currentAppointment]);

  useEffect(() => {
    let isMounted = true;
    const setDefaults = (mappedTypes, hasMissingEligibility = false) => {
      if (!currentUser) {
        setSelectedAppointmentType(null);
        setBookingEligibilityMissing(false);
        setEligibilityCheckLoading(false);
        return;
      }
      
      if (hasMissingEligibility) {
        setBookingEligibilityMissing(true);
        setSelectedAppointmentType(null);
        setEligibilityCheckLoading(false);
        return;
      }
      
      if (mappedTypes && mappedTypes.length > 0) {
        setBookingEligibilityMissing(false);
        if (mappedTypes.length === 1) {
          setSelectedAppointmentType(mappedTypes[0]);
        } else {
          setSelectedAppointmentType(null);
        }
        setEligibilityCheckLoading(false);
        return;
      }
      
      const fallback = appointmentTypes.find(
        (item) => item.donorStatus === currentUser.status
      );
      if (fallback) {
        setSelectedAppointmentType(fallback);
        setBookingEligibilityMissing(false);
      }
      setEligibilityCheckLoading(false);
    };

    if (currentUser) {
      setEligibilityCheckLoading(true);

      (async () => {
        try {
          const res = await get(`/timeslots/getEligibleAppointmentTypesForStaff/${currentUser.id}`);
          const items = (res?.content || []).map(entry => ({
            ...entry.appointmentType,
            bookingChecks: entry.bookingChecks || entry.appointmentType?.bookingChecks || null,
            bookableFromDate: entry.bookableFromDate || null,
            donorQuarantine: entry.donorQuarantine || null,
            eligibility: entry.eligibility || null,
          }));
          if (!isMounted) return;
          setEligibleAppointmentTypes(items);
          setDefaults(items, false);
        } catch (err) {
          const isMissingEligibility = err?.body?.error === 'booking_eligibility_missing';
          if (!isMounted) return;
          setEligibleAppointmentTypes([]);
          setDefaults([], isMissingEligibility);
        }
      })();
    } else {
      setEligibleAppointmentTypes([]);
      setDefaults([], false);
    }

    return () => { isMounted = false };
  }, [currentUser, currentDate, appointmentTypes]);

  useEffect(() => {
    setCurrentAppointments(
      selectedTimeslot?.appointments?.filter((item) => item.status === "pending")
        .map((item) => {
          return {
            label: `${dateFns.format(new Date(item.startDate), "HH:mm")} - ${dateFns.format(new Date(item.endDate), "HH:mm")}`,
            value: String(item.startDate),
          };
        }) || [],
    );

    setIsConsultationAppointment(selectedTimeslot?.appointmentType.name === "Consultation" && !isSlotClickFromCell);

  }, [selectedTimeslot, isSlotClickFromCell]);

  const getTimeslotLimits = () => {
    const startDate = workingTimeToTimestamp(timeFrom, currentDate);
    const endDate = workingTimeToTimestamp(timeTo, currentDate);
    return { startDate, endDate };
  };

  const getTimeslotRequestBody = (checkForAvailableResources) => {
    if (!selectedAppointmentType) {
      return null;
    }
    const { startDate, endDate } = getTimeslotLimits();
    const bloodTypes = isNewRegistrationOrPendingRegistration || !currentUser?.donorType
      ? ALL_BLOOD_TYPES
      : [currentUser.donorType];

    return {
      ...(!checkForAvailableResources && {
        resourceId: selectedResourceId || selectedTimeslot?.resourceId,
      }),
      donorType: bloodTypes,
      appointmentTypeId: selectedAppointmentType.appointmentTypeId,
      startDate,
      endDate,
      exceptionDates: [],
      exceptionRules: [],
      exceptionHoursOnDates: [],
    };
  };

  const handleSubmit = async () => {
    if (!selectedAppointmentType) {
      return;
    }

    if (inlineWarnings.length > 0 && !inlineOverrideAllowed) {
      return;
    }

    const hasWarnings = inlineWarnings.length > 0;

    const snapshotUser = currentUser;
    const snapshotAppointmentType = selectedAppointmentType;
    const snapshotIsConsultation = isConsultationAppointment;
    const snapshotIsDonorStatusPending = isDonorStatusPending;

    const hasExistingAppointment = snapshotUser?.nextAppointment != null;

    const cancelExistingAppointment = async () => {
      if (hasExistingAppointment) {
        await cancelAppointmentBooking(
          bloodBankId,
          snapshotUser.nextAppointment.reservationId,
          snapshotUser.id,
          snapshotUser.nextAppointment.startDate,
          bloodBankName,
        );
      }
    };

    const snapshotUpdateDonorStatus = async () => {
      try {
        if (snapshotIsConsultation && !snapshotIsDonorStatusPending) {
          await updateDonor({
            ...snapshotUser,
            status: "pending",
          });
        }
      } catch (error) {
        console.log(error);
      }
    };

    let timeSlotData = selectedTimeslot;
    let requestBodyData = getTimeslotRequestBody();

    const appointmentValue = isSlotClickFromCell ? requestBodyData?.startDate : chosenAppointment.value;
    const bookingStart = Number(appointmentValue);
    const bookingEnd = bookingStart + snapshotAppointmentType.duration;

    const needsCustomTimeslot =
      !isSlotClickFromCell &&
      (snapshotAppointmentType.appointmentTypeId !== timeSlotData?.appointmentType?.appointmentTypeId ||
        snapshotAppointmentType.duration !== timeSlotData?.appointmentType?.duration);

    if (needsCustomTimeslot && requestBodyData) {
      requestBodyData = {
        ...requestBodyData,
        startDate: bookingStart,
        endDate: bookingEnd,
      };
    }

    const overlapResult = await handleOverlaps({
      bookingStart,
      bookingEnd,
      currentTimeslotId: timeSlotData?.timeslotId,
      applyAvailableExceptions: needsCustomTimeslot,
      deferExceptions: isSlotClickFromCell || needsCustomTimeslot,
    });
    if (overlapResult?.blocked) {
      return;
    }
    const deferredOverlaps = overlapResult?.deferredOverlaps || [];

    toggleBookAppointmentModal();
    startOverwriteTimeslot();

    try {
      await cancelExistingAppointment();

      await applyOverlapExceptions(deferredOverlaps);

      if (isSlotClickFromCell) {
        setIsDirectlyBooking(true);
        timeSlotData = await setTimeslotRequest(
          requestBodyData,
          snapshotAppointmentType.duration,
          true,
        );
        if (timeSlotData) {
          timeSlotData.appointments = [
            {
              appointmentType: timeSlotData.appointmentType,
              donorType: timeSlotData.donorType,
              startDate: timeSlotData.startDate,
              endDate: timeSlotData.endDate,
              startDateISO: timeSlotData.startDateISO,
              status: "pending",
              timeslotId: timeSlotData.timeslotId,
              timeslotStartDate: timeSlotData.startDate,
            },
          ];
        }

        await bookAppointment(
          bookingStart,
          timeSlotData?.timeslotId,
          snapshotUser.id,
          bloodBankId,
          hasWarnings,
        );
      } else if (needsCustomTimeslot) {
        const createdTimeslot = await setTimeslotRequest(
          requestBodyData,
          snapshotAppointmentType.duration,
          true,
        );
        if (!createdTimeslot?.timeslotId) {
          finishOverwriteTimeslot();
          setIsDirectlyBooking(false);
          return;
        }

        await bookAppointment(
          bookingStart,
          createdTimeslot.timeslotId,
          snapshotUser.id,
          bloodBankId,
          hasWarnings,
        );
      } else {
        await bookAppointment(
          bookingStart,
          timeSlotData?.timeslotId,
          snapshotUser.id,
          bloodBankId,
          hasWarnings,
        );
      }

      await snapshotUpdateDonorStatus();
    } catch (err) {
      console.error('Booking failed:', err);
    } finally {
      finishOverwriteTimeslot();
      setIsDirectlyBooking(false);
    }
  };

  const handleModalClose = () => {
    toggleBookAppointmentModal();
  };

  useEffect(() => {
    const initialChosenAppointmentValue = isDayPage ? currentAppointments.find(appointment => Number(appointment.value) === currentAppointment?.startDate) : null;
    
    setUserPnr("");
    setCurrentUser(null);
    setIsDonorStatusPending(false);
    setIsConsultationAppointment(false);
    setSelectedAppointmentType(null);
    setEligibleAppointmentTypes([]);
    setEligibilityCheckLoading(false);
    setBookingEligibilityMissing(false);
    setInlineWarnings([]);
    setInlineOverrideAllowed(true);
    if (!bookAppointmentModalIsOpen) {
      setChosenAppointment(initialChosenAppointmentValue);
    } else {
      if (currentAppointments.length === 1) {
        setChosenAppointment(currentAppointments[0]);
      }
    }
  }, [bookAppointmentModalIsOpen, currentAppointments, currentAppointment?.startDate, isDayPage]);

  const formatText = (text) => {
    const firstPart = text.slice(0, 6);
    const secondPart = text.slice(6, 11);
    return `${firstPart}\u00A0${secondPart}`;
  };

  const handleCopy = (e) => {
    e.clipboardData.setData("text/plain", userPnr);
    e.preventDefault();
  };

  const isBookDisabled = () => {
    if (eligibilityCheckLoading || warningsLoading) {
      return true;
    }

    if (bookingEligibilityMissing) {
      return true;
    }

    if (currentUser === null) {
      return true;
    }

    if (eligibleAppointmentTypes.length > 0 && !selectedAppointmentType) {
      return true;
    }

    if (inlineWarnings.length > 0 && !inlineOverrideAllowed) {
      return true;
    }

    if (isConsultationAppointment) {
      return false;
    }

    if (!selectedAppointmentType) {
      return true;
    }

    return !isSlotClickFromCell && chosenAppointment === null;
  };

  return (
    <>
      <Modal
        isModalOpen={bookAppointmentModalIsOpen}
        handleModalClose={handleModalClose}
        {...props}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            {t(`${PREFIX}.header`)}
          </h2>
          <button className="modal-close" onClick={handleModalClose}>
            <span className="icon-close" />
          </button>
        </div>

        <div className="modal-body modal-content">
          <div className="form-group d-flex items-center">
            <div className="flex-grow-0 modal-label">
              {t(`${PREFIX}.id`)}
            </div>
            <div className="flex-grow-1">
              <Search
                type="text"
                placeholder={t(`${PREFIX}.searchPlaceholder`)}
                shortSearch={true}
                setValue={setUserPnr}
                autoComplete={"off"}
                isPnrSearch={true}
              />
            </div>
          </div>

          <div className={`form-group modal-text ${currentUser || "thin"}`}>
            {currentUser ? (
              `${currentUser.firstname} ${currentUser.lastname}`
            ) : userPnr === "" ? (
              ""
            ) : (
              <>
                <span>
                  {t(`${PREFIX}.noMatch`)}
                </span>
                <span onCopy={handleCopy}>{` ${formatText(userPnr)}`}</span>
              </>
            )}
          </div>

          {currentAppointments.length > 1 && currentUser && (
            <div className="form-group">
              <CustomSelect
                label={t(`${PREFIX}.selectAppointmentTime`)}
                data={currentAppointments}
                value={chosenAppointment}
                onChange={setChosenAppointment}
                isSearchable={true}
              />
            </div>
          )}

          {/* Appointment type selector */}
          {currentUser && eligibleAppointmentTypes.length > 0 && (
            <div className="form-group">
              <CustomSelect
                label={t(`${PREFIX}.appointmentType`)}
                data={eligibleAppointmentTypes.map((it) => ({ label: getAppointmentTypeDisplayName(it), value: it }))}
                value={selectedAppointmentType ? { label: getAppointmentTypeDisplayName(selectedAppointmentType), value: selectedAppointmentType } : null}
                onChange={(val) => setSelectedAppointmentType(val?.value || null)}
                isSearchable={true}
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.value?.appointmentTypeId || opt.value?.name || 'placeholder'}
              />
            </div>
          )}

          {bookingEligibilityMissing && (
            <div className="form-group modal-text thin error">
              {t(`${PREFIX}.bookingEligibilityMissing`)}
            </div>
          )}

          {inlineWarnings.length > 0 && (
            <div className="modal-warnings">
              {inlineWarnings.map((v) => (
                <div key={v.key} className="modal-msg modal-warning-item">
                  <span className="modal-warning-dot" />
                  <span>{v.message}</span>
                </div>
              ))}

              {!inlineOverrideAllowed && (
                <div className="modal-msg modal-warning-blocked">
                  {t(`${PREFIX}.forceBookNotAllowed`)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className="inputs-group">
            <div className="inputs-group-item">
              <Button
                type="button"
                text={t("common.cancel")}
                onClick={toggleBookAppointmentModal}
              />
            </div>
            <div className="inputs-group-item">
              <Button
                disabled={isBookDisabled()}
                btnClassName="btn-primary"
                type="button"
                text={t("common.book")}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        isModalOpen={overlapModalOpen}
        handleModalClose={() => setOverlapModalOpen(false)}
        {...props}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            {t(`${PREFIX}.overlapTitle`) || "Cannot book appointment"}
          </h2>
          <button
            className="modal-close"
            onClick={() => setOverlapModalOpen(false)}
          >
            <span className="icon-close" />
          </button>
        </div>
        <div className="modal-body modal-content">
          <div className="modal-msg">
            {t(`${PREFIX}.overlapReserved`)}
          </div>
        </div>
        <div className="modal-footer light w-100">
          <div className="inputs-group">
            <div className="inputs-group-item">
              <Button
                type="button"
                text={t("common.ok")}
                btnClassName="m-10"
                onClick={() => setOverlapModalOpen(false)}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BookAppointment;
